import mapValues from 'lodash/mapValues'
import { NFCS_MINT_STATUS_CHANGE_EVENT } from 'modules/analytics/events'
import { useFirebase } from 'modules/analytics/firebase'
import { useTransactionDetails } from 'modules/blockChain/hooks/useTransactionDetails'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useNFCSInfo } from 'modules/nfcs/hooks/useNFCSInfo'
import { PoolRisk } from 'modules/pools/constants/PoolRisk'
import { NFCSFormSings, NFCSFormStep, NFCSStatus } from 'modules/nfcs/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSimpleReducer } from 'shared/hooks/useSimpleReducer'
import { isBadNFCSScore, isFraudNFCSScore } from '../utils/NFCSScores'
import { NFCSStateContext } from './NFCSStateContext'

type Props = {
  children?: React.ReactNode
}

export const NFCSStateProvider = ({ children }: Props) => {
  const { walletAddress } = useWeb3()
  const [txHash, setTxHash] = useState<string | null>(null)
  const [currentFormStep, setCurrentFormStep] = useState(NFCSFormStep.Start)
  const [signs, setSigns] = useSimpleReducer<NFCSFormSings>({})
  const { trackEvent } = useFirebase()
  const { txStatus, resetTransaction: resetTx } = useTransactionDetails({
    hash: txHash,
    defaultStatus: 'empty',
  })

  const {
    mutate,
    error,
    isLoading,
    nfcs,
    tokenId,
    primaryBundleAddress,
    isPrimaryAddress,
    nfcsBundle,
  } = useNFCSInfo({
    address: walletAddress,
    shouldRevalidate: txStatus.isPending || txStatus.isSuccess,
  })

  const signedAddresses = useMemo(
    () => Object.keys(signs).filter(address => signs[address] !== undefined),
    [signs],
  )

  const nfcsStatus: NFCSStatus = useMemo(() => {
    const httpStatus = error?.status
    if (
      httpStatus &&
      (httpStatus >= 500 || httpStatus === 404 || httpStatus === 408)
    ) {
      return 'server-error'
    } else if (httpStatus === 429) {
      return 'rate-limit'
    } else if (isLoading && txStatus.isEmpty) {
      return 'loading'
    } else if (txStatus.isFailed) {
      return 'failed'
    } else if ((txStatus.isPending || txStatus.isSuccess) && nfcs === -1) {
      return 'generating'
    } else if (nfcs === -1) {
      return 'not-generated'
    }

    return 'generated'
  }, [
    error?.status,
    isLoading,
    txStatus.isEmpty,
    txStatus.isFailed,
    txStatus.isPending,
    txStatus.isSuccess,
    nfcs,
  ])

  const revalidateScore = useCallback(async () => {
    if (!txStatus.isEmpty) {
      setTxHash(null)
      resetTx()
    }
    await mutate()
  }, [txStatus.isEmpty, mutate, setTxHash, resetTx])

  const risk = useMemo(() => {
    if (nfcs === -1) return null

    let result: PoolRisk | null = null

    if (isFraudNFCSScore(nfcs) || isBadNFCSScore(nfcs)) {
      result = null
    } else if (nfcs >= 7) {
      result = PoolRisk.HIGH
    } else if (nfcs >= 4) {
      result = PoolRisk.MIDDLE
    } else if (nfcs > 0) {
      result = PoolRisk.LOW
    }
    return result
  }, [nfcs])

  const handleCurrentFormStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex === NFCSFormStep.Start) {
        setSigns(mapValues(signs, () => undefined))
      }
      setCurrentFormStep(stepIndex)
    },
    [setCurrentFormStep, setSigns, signs],
  )

  useEffect(() => {
    setTxHash(null)
  }, [walletAddress])

  useEffect(() => {
    if (!txStatus.isEmpty) {
      trackEvent(NFCS_MINT_STATUS_CHANGE_EVENT, { status: nfcsStatus })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfcsStatus])

  return (
    <NFCSStateContext.Provider
      value={{
        signs,
        signedAddresses,
        txHash,
        txStatus,
        tokenId,
        primaryBundleAddress,
        isPrimaryAddress,
        nfcs,
        nfcsStatus,
        risk,
        currentFormStep,
        nfcsBundle,
        handleCurrentFormStepChange,
        setSigns,
        setTxHash,
        resetTx,
        revalidateScore,
      }}
    >
      {children}
    </NFCSStateContext.Provider>
  )
}
